"use client";

import * as React from "react";
import { VerticalTabs, type VerticalTabItem } from "@/src/components/navigation/vertical-tabs";
import { Text } from "@/src/components";
import { ComponentPage } from "@/components/docs/component-page";

export default function DocsComponentsVerticalTabs() {
  const items: VerticalTabItem[] = [
    {
      value: "profile",
      label: "Profile",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Profile</Text>
          <Text as="p" className="text-muted-foreground">
            Update your personal information, such as name, avatar and bio.
          </Text>
        </div>
      ),
    },
    {
      value: "security",
      label: "Security",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Security</Text>
          <Text as="p" className="text-muted-foreground">
            Manage your password and two‑factor authentication settings.
          </Text>
        </div>
      ),
    },
    {
      value: "notifications",
      label: "Notifications",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Notifications</Text>
          <Text as="p" className="text-muted-foreground">
            Choose how and when you want to be notified about activity.
          </Text>
        </div>
      ),
    },
  ];

  const code = `import { VerticalTabs, type VerticalTabItem } from "@/registry/new-york/blocks/tabs/vertical-tabs";
import { Text } from "@/registry/new-york/ui/text";

export function VerticalTabsDemo() {
  const items: VerticalTabItem[] = [
    {
      value: "profile",
      label: "Profile",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Profile</Text>
          <Text as="p" className="text-muted-foreground">
            Update your personal information, such as name, avatar and bio.
          </Text>
        </div>
      ),
    },
    {
      value: "security",
      label: "Security",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Security</Text>
          <Text as="p" className="text-muted-foreground">
            Manage your password and two‑factor authentication settings.
          </Text>
        </div>
      ),
    },
    {
      value: "notifications",
      label: "Notifications",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Notifications</Text>
          <Text as="p" className="text-muted-foreground">
            Choose how and when you want to be notified about activity.
          </Text>
        </div>
      ),
    },
  ];

  return <VerticalTabs defaultValue="profile" items={items} />;
}`;

  const installCommand = `npx shadcn@latest add @bluecopa-ui/vertical-tabs`;

  return (
    <ComponentPage
      title="Vertical Tabs"
      description="Vertical tabbed interface with tabs on the left and content on the right."
      preview={
        <div className="h-[400px] w-full">
          <VerticalTabs defaultValue="profile" items={items} />
        </div>
      }
      code={code}
      installCommand={installCommand}
      apiReference={{
        name: "VerticalTabsProps",
        props: [
          {
            name: "items",
            type: "VerticalTabItem[]",
            description: "Array of tab items, each with value, label, and content.",
          },
          {
            name: "value",
            type: "string",
            description: "Controlled value for the active tab.",
          },
          {
            name: "defaultValue",
            type: "string",
            description: "Default value for uncontrolled usage.",
          },
          {
            name: "onValueChange",
            type: "(value: string) => void",
            description: "Callback fired when the active tab changes.",
          },
          {
            name: "verticalText",
            type: "boolean",
            description: "Whether to display text vertically in tabs. Defaults to true.",
          },
          {
            name: "className",
            type: "string",
            description: "Additional CSS classes for the root container.",
          },
          {
            name: "listClassName",
            type: "string",
            description: "Additional CSS classes for the tabs list.",
          },
          {
            name: "contentContainerClassName",
            type: "string",
            description: "Additional CSS classes for the content container.",
          },
        ],
      }}
    />
  );
}

